// ============================================================================
// GERENCIAMENTO DE NOTÍCIAS E ARTIGOS - DASHBOARD ADMIN
// ============================================================================
// Arquivo: src/components/Dashboard_ADM/GerenciamentoNoticiasArtigos.tsx
// UPDATED: 2026-04-07 — galeria de imagens múltiplas no modal criar e editar
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Search, Plus, Trash2, Eye, Calendar, User, Edit, Image,
  Image as ImageIcon, FileText, CheckCircle, Clock, XCircle, TrendingUp,
  Loader2, AlertTriangle, Send, Upload, Link as LinkIcon,
  ExternalLink, X, Archive, Globe, ImagePlus,
} from 'lucide-react';
import { API_BASE, authH } from '../../utils/apiConfig';

// ─── Types ───────────────────────────────────────────────────────────────────
type NoticiaCategoria =
  | 'announcement' | 'event' | 'tutorial'
  | 'industry_news' | 'company_news' | 'member_spotlight';
type NoticiaStatus = 'published' | 'draft' | 'scheduled' | 'archived';

interface Noticia {
  id: string;
  title: string;
  subtitle?: string;
  summary: string;
  content: string;
  slug: string;
  author: { id: string; name: string };
  category: NoticiaCategoria;
  status: NoticiaStatus;
  publishedAt: string | null;
  featuredImageUrl?: string;
  externalUrl?: string;
  viewsCount: number;
  likesCount: number;
  tags: string[];
  metadata?: { galleryImages?: string[]; readingTime?: number };
}

interface NoticiaStats {
  total: number; published: number; draft: number; scheduled: number; totalViews: number;
}

interface NovaNoticiaForm {
  title: string; subtitle: string; summary: string; content: string;
  category: NoticiaCategoria; featuredImageUrl: string; externalUrl: string;
  authorName: string; tags: string[]; galleryImages: string[];
}

// ─── Componente de Galeria ────────────────────────────────────────────────────
const GaleriaUpload: React.FC<{
  images: string[];
  onChange: (imgs: string[]) => void;
}> = ({ images, onChange }) => {
  const [urlInput, setUrlInput]       = useState('');
  const [uploading, setUploading]     = useState(false);
  const fileRef                       = useRef<HTMLInputElement>(null);

  const addUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    onChange([...images, url]);
    setUrlInput('');
  };

  const removeImg = (idx: number) => onChange(images.filter((_, i) => i !== idx));

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls: string[] = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        if (file.size > 5 * 1024 * 1024) { alert(`${file.name} > 5MB, ignorado.`); continue; }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('folder', 'news-gallery');
        const auth = authH() as Record<string, string>;
        const res = await fetch(`${API_BASE}/upload`, {
          method: 'POST',
          headers: { Authorization: auth['Authorization'] },
          body: fd,
        });
        if (res.ok) {
          const d = await res.json();
          if (d.url) urls.push(d.url);
        }
      }
      if (urls.length) onChange([...images, ...urls]);
    } catch { alert('Erro ao fazer upload de uma ou mais imagens.'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-3">
      {/* Adicionar por URL */}
      <div className="flex gap-2">
        <input
          type="url"
          value={urlInput}
          onChange={e => setUrlInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addUrl())}
          placeholder="https://exemplo.com/imagem.jpg"
          className="flex-1 px-4 py-2.5 border-2 border-gray-300 rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-blue-500"
        />
        <button type="button" onClick={addUrl}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-all flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </div>

      {/* Upload de arquivo */}
      <div>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFile} className="hidden" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="w-full py-2.5 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
          {uploading
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
            : <><Upload className="w-4 h-4" /> Upload de imagens (pode selecionar várias)</>}
        </button>
      </div>

      {/* Grade de imagens adicionadas */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-2">
          {images.map((url, idx) => (
            <div key={idx} className="relative group rounded-xl overflow-hidden border border-gray-200 aspect-video bg-gray-100">
              <img src={url} alt={`img-${idx}`} className="w-full h-full object-cover"
                onError={e => { (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="60"><rect fill="%23eee" width="100" height="60"/><text fill="%23aaa" x="50%" y="55%" text-anchor="middle" font-size="10">erro</text></svg>'; }} />
              <button type="button" onClick={() => removeImg(idx)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs hover:bg-red-700">
                <X className="w-3 h-3" />
              </button>
              <span className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[9px] px-1 py-0.5 truncate">
                {idx + 1}. {url.split('/').pop()}
              </span>
            </div>
          ))}
        </div>
      )}

      {images.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-2">Nenhuma imagem adicionada ainda</p>
      )}
    </div>
  );
};

// ─── Component Principal ──────────────────────────────────────────────────────
const GerenciamentoNoticiasArtigos: React.FC = () => {
  const [noticias, setNoticias]               = useState<Noticia[]>([]);
  const [statsData, setStatsData]             = useState<NoticiaStats | null>(null);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState<string | null>(null);
  const [busca, setBusca]                     = useState('');
  const [buscaInput, setBuscaInput]           = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todas');
  const [filtroStatus, setFiltroStatus]       = useState<string>('todos');
  const [noticiaSelecionada, setNoticiaSelecionada] = useState<Noticia | null>(null);
  const [modalAberto, setModalAberto]         = useState(false);
  const [modalCriar, setModalCriar]           = useState(false);
  const [salvando, setSalvando]               = useState(false);
  const [uploadingImage, setUploadingImage]   = useState(false);
  const fileInputRef                          = useRef<HTMLInputElement>(null);

  const [modalEditar, setModalEditarNoticia]      = useState(false);
  const [formEditarNoticia, setFormEditarNoticia] = useState<Partial<NovaNoticiaForm>>({});
  const [noticiaEditando, setNoticiaEditando]     = useState<Noticia | null>(null);

  const [novaNoticia, setNovaNoticia] = useState<NovaNoticiaForm>({
    title:'', subtitle:'', summary:'', content:'', category:'announcement',
    featuredImageUrl:'', externalUrl:'', authorName:'', tags:[], galleryImages:[],
  });

  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaInput), 400);
    return () => clearTimeout(t);
  }, [buscaInput]);

  // ─── Fetch ───────────────────────────────────────────────────────────────
  const fetchNoticias = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const url = busca.trim().length >= 2
        ? `${API_BASE}/news/search?query=${encodeURIComponent(busca)}&page=1&limit=50`
        : `${API_BASE}/news?page=1&limit=50`;
      const res = await fetch(url, { headers: authH() });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      setNoticias(data.news ?? []);
    } catch (e: any) { setError(e.message ?? 'Erro ao carregar notícias'); }
    finally { setLoading(false); }
  }, [busca]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/news/stats/summary`);
      if (!res.ok) return;
      const d = await res.json();
      setStatsData({ total:d.total??0, published:d.published??0, draft:d.draft??0, scheduled:d.scheduled??0, totalViews:d.totalViews??0 });
    } catch { /* opcional */ }
  }, []);

  useEffect(() => { fetchNoticias(); }, [fetchNoticias]);
  useEffect(() => { fetchStats(); },   [fetchStats]);

  // ─── Upload capa ──────────────────────────────────────────────────────────
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Selecione apenas imagens'); return; }
    if (file.size > 5 * 1024 * 1024)    { alert('Máximo 5MB'); return; }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'news');
      const auth = authH() as Record<string, string>;
      const res  = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: { Authorization: auth['Authorization'] },
        body: formData,
      });
      if (!res.ok) throw new Error('Erro no upload');
      const data = await res.json();
      setNovaNoticia(prev => ({ ...prev, featuredImageUrl: data.url }));
    } catch (err: any) { alert(err.message ?? 'Erro ao fazer upload'); }
    finally { setUploadingImage(false); }
  };

  // ─── CRUD ────────────────────────────────────────────────────────────────
  const handleCriar = async () => {
    if (!novaNoticia.title.trim() || !novaNoticia.content.trim()) { alert('Preencha título e conteúdo'); return; }
    setSalvando(true);
    try {
    const resCreate = await fetch(`${API_BASE}/news`, {
        method: 'POST',
        headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title:            novaNoticia.title,
          subtitle:         novaNoticia.subtitle         || undefined,
          summary:          novaNoticia.summary          || undefined,
          content:          novaNoticia.content,
          category:         novaNoticia.category,
          featuredImageUrl: novaNoticia.featuredImageUrl || undefined,
          externalUrl:      novaNoticia.externalUrl      || undefined,
          authorName:       novaNoticia.authorName       || undefined,
          tags:             novaNoticia.tags,
        }),
      });
      if (!resCreate.ok) {
        const err = await resCreate.json().catch(() => ({}));
        throw new Error(Array.isArray(err.message) ? err.message.join(', ') : err.message ?? 'Erro ao criar');
      }
      const created   = await resCreate.json();
      const noticiaId = created.id ?? created.news?.id;
      if (noticiaId) {
        const resPub = await fetch(`${API_BASE}/news/${noticiaId}/publish`, { method: 'POST', headers: authH() });
        if (!resPub.ok) throw new Error('Notícia criada, mas falhou ao publicar');

        // Salva galeria separadamente via PATCH (DTO de criação não aceita galleryImages ainda)
        if (novaNoticia.galleryImages.length > 0) {
          await fetch(`${API_BASE}/news/${noticiaId}`, {
            method: 'PATCH',
            headers: { ...authH(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ galleryImages: novaNoticia.galleryImages }),
          });
        }
      }
      alert('Notícia publicada com sucesso!');
      setModalCriar(false);
      setNovaNoticia({ title:'', subtitle:'', summary:'', content:'', category:'announcement', featuredImageUrl:'', externalUrl:'', authorName:'', tags:[], galleryImages:[] });
      fetchNoticias(); fetchStats();
    } catch (err: any) { alert(err.message ?? 'Erro ao criar'); }
    finally { setSalvando(false); }
  };

  const handlePublicar = async (id: string) => {
    if (!confirm('Publicar esta notícia?')) return;
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/news/${id}/publish`, { method: 'POST', headers: authH() });
      if (!res.ok) throw new Error('Erro ao publicar');
      alert('Notícia publicada!');
      fetchNoticias(); fetchStats(); setModalAberto(false);
    } catch (err: any) { alert(err.message ?? 'Erro ao publicar'); }
    finally { setSalvando(false); }
  };

  const handleArquivar = async (id: string) => {
    if (!confirm('Arquivar esta notícia?')) return;
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/news/${id}`, {
        method: 'PATCH',
        headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' }),
      });
      if (!res.ok) throw new Error('Erro ao arquivar');
      alert('Notícia arquivada.');
      fetchNoticias(); fetchStats(); setModalAberto(false);
    } catch (err: any) { alert(err.message ?? 'Erro ao arquivar'); }
    finally { setSalvando(false); }
  };

  const handleDeletar = async (id: string, titulo: string) => {
    if (!confirm(`Deletar permanentemente "${titulo}"?`)) return;
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/news/${id}`, { method: 'DELETE', headers: authH() });
      if (!res.ok) throw new Error('Erro ao deletar');
      alert('Notícia deletada!');
      if (modalAberto) setModalAberto(false);
      fetchNoticias(); fetchStats();
    } catch (err: any) { alert(err.message ?? 'Erro ao deletar'); }
    finally { setSalvando(false); }
  };

  const handleSalvarEdicaoNoticia = async () => {
    if (!noticiaEditando) return;
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/news/${noticiaEditando.id}`, {
        method: 'PATCH',
        headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title:            formEditarNoticia.title            || undefined,
          subtitle:         formEditarNoticia.subtitle         || undefined,
          summary:          formEditarNoticia.summary          || undefined,
          content:          formEditarNoticia.content          || undefined,
          featuredImageUrl: formEditarNoticia.featuredImageUrl || undefined,
          externalUrl:      formEditarNoticia.externalUrl      || undefined,
          category:         formEditarNoticia.category         || undefined,
          galleryImages:    formEditarNoticia.galleryImages    ?? [],
        }),
      });
      if (!res.ok) throw new Error('Erro ao salvar');
      alert('Notícia atualizada!');
      setModalEditarNoticia(false);
      fetchNoticias();
    } catch (err: any) { alert(err.message ?? 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  // ─── Filtros ─────────────────────────────────────────────────────────────
  const noticiasFiltradas = useMemo(() => noticias.filter(n => {
    if (filtroCategoria !== 'todas' && n.category !== filtroCategoria) return false;
    if (filtroStatus !== 'todos'    && n.status   !== filtroStatus)    return false;
    return true;
  }), [noticias, filtroCategoria, filtroStatus]);

  // ─── UI Helpers ──────────────────────────────────────────────────────────
  const getCategoriaConfig = (cat: NoticiaCategoria) => {
    const configs = {
      announcement:    { label:'Comunicado', bg:'bg-blue-50',   text:'text-blue-700',   border:'border-blue-200',   icon:FileText },
      event:           { label:'Evento',     bg:'bg-purple-50', text:'text-purple-700', border:'border-purple-200', icon:Calendar },
      tutorial:        { label:'Tutorial',   bg:'bg-green-50',  text:'text-green-700',  border:'border-green-200',  icon:FileText },
      industry_news:   { label:'Notícia',    bg:'bg-orange-50', text:'text-orange-700', border:'border-orange-200', icon:TrendingUp },
      company_news:    { label:'Empresa',    bg:'bg-indigo-50', text:'text-indigo-700', border:'border-indigo-200', icon:FileText },
      member_spotlight:{ label:'Destaque',   bg:'bg-amber-50',  text:'text-amber-700',  border:'border-amber-200',  icon:TrendingUp },
    };
    return configs[cat] ?? configs.announcement;
  };

  const getStatusBadge = (status: NoticiaStatus) => {
    const configs = {
      published: { label:'Publicado', bg:'bg-green-100', text:'text-green-800', icon:CheckCircle },
      draft:     { label:'Rascunho',  bg:'bg-gray-100',  text:'text-gray-800',  icon:FileText },
      scheduled: { label:'Agendado',  bg:'bg-blue-100',  text:'text-blue-800',  icon:Clock },
      archived:  { label:'Arquivado', bg:'bg-red-100',   text:'text-red-800',   icon:XCircle },
    };
    const config = configs[status] ?? configs.draft;
    const Icon   = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${config.bg} ${config.text}`}>
        <Icon className="w-3.5 h-3.5" /> {config.label}
      </span>
    );
  };

  const getNoticiaUrl = (slug: string) => `${window.location.origin}/noticias/${slug}`;

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="p-8 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">

      <div className="mb-8">
        <h1 className="text-4xl font-black text-gray-900 mb-2">Gerenciamento de Notícias</h1>
        <p className="text-gray-600">Gerencie publicações, artigos e comunicados</p>
      </div>

      {/* STATS */}
      {statsData && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          {[
            { label:'Total',         value:statsData.total,                       icon:FileText,    color:'blue' },
            { label:'Publicados',    value:statsData.published,                   icon:CheckCircle, color:'green' },
            { label:'Rascunhos',     value:statsData.draft,                       icon:FileText,    color:'gray' },
            { label:'Agendados',     value:statsData.scheduled,                   icon:Clock,       color:'purple' },
            { label:'Visualizações', value:statsData.totalViews.toLocaleString(), icon:Eye,         color:'orange' },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="bg-white rounded-2xl border-2 border-gray-200 p-6 hover:shadow-lg transition-all">
                <div className={`w-12 h-12 rounded-xl bg-${stat.color}-100 flex items-center justify-center mb-4`}>
                  <Icon className={`w-6 h-6 text-${stat.color}-600`} />
                </div>
                <p className="text-3xl font-black text-gray-900 mb-1">{stat.value}</p>
                <p className="text-sm font-semibold text-gray-600">{stat.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 mb-8">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[300px] relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input value={buscaInput} onChange={e => setBuscaInput(e.target.value)}
              placeholder="Buscar notícias..."
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)}
            className="px-4 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700">
            <option value="todas">Todas Categorias</option>
            <option value="announcement">Comunicado</option>
            <option value="event">Evento</option>
            <option value="tutorial">Tutorial</option>
            <option value="industry_news">Notícia</option>
            <option value="company_news">Empresa</option>
            <option value="member_spotlight">Destaque</option>
          </select>
          <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
            className="px-4 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700">
            <option value="todos">Todos Status</option>
            <option value="published">Publicado</option>
            <option value="draft">Rascunho</option>
            <option value="scheduled">Agendado</option>
            <option value="archived">Arquivado</option>
          </select>
          <button onClick={() => setModalCriar(true)}
            className="px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all flex items-center gap-2">
            <Plus className="w-5 h-5" /> Nova Publicação
          </button>
        </div>
      </div>

      {loading && <div className="flex items-center justify-center py-20"><Loader2 className="w-12 h-12 text-blue-600 animate-spin" /></div>}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 flex items-center gap-4">
          <AlertTriangle className="w-8 h-8 text-red-600" />
          <div><p className="font-bold text-red-900">Erro ao carregar</p><p className="text-red-700">{error}</p></div>
        </div>
      )}

      {/* GRID DE NOTÍCIAS */}
      {!loading && !error && noticiasFiltradas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {noticiasFiltradas.map(noticia => {
            const catConfig = getCategoriaConfig(noticia.category);
            const CatIcon   = catConfig.icon;
            const galeria   = noticia.metadata?.galleryImages ?? [];
            return (
              <div key={noticia.id} className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-all flex flex-col">
                <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {noticia.featuredImageUrl
                    ? <img src={noticia.featuredImageUrl} alt="" className="w-full h-full object-cover" />
                    : <ImageIcon className="w-16 h-16 text-gray-400" />}
                </div>

                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${catConfig.bg} ${catConfig.text} ${catConfig.border}`}>
                      <CatIcon className="w-3.5 h-3.5" />{catConfig.label}
                    </span>
                    {getStatusBadge(noticia.status)}
                    {galeria.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">
                        <Image className="w-3 h-3" /> {galeria.length}
                      </span>
                    )}
                  </div>

                  <h3 className="text-lg font-black text-gray-900 mb-2 line-clamp-2">{noticia.title}</h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-3 flex-1">{noticia.summary}</p>

                  {noticia.status === 'published' && (
                    <a href={getNoticiaUrl(noticia.slug)} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-semibold mb-3 pb-3 border-b border-gray-100">
                      <LinkIcon className="w-4 h-4" /> Ver no portal <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {noticia.externalUrl && (
                    <a href={noticia.externalUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-800 font-semibold mb-3 pb-3 border-b border-gray-100 truncate">
                      <Globe className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">Link de referência</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  )}

                  <div className="space-y-1.5 mb-4 pb-4 border-b border-gray-100 text-xs text-gray-500">
                    <div className="flex items-center gap-2"><User className="w-3.5 h-3.5" /><span>{noticia.author?.name ?? 'Admin'}</span></div>
                    <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /><span>{noticia.publishedAt ? new Date(noticia.publishedAt).toLocaleDateString('pt-BR') : '—'}</span></div>
                    <div className="flex items-center gap-2"><Eye className="w-3.5 h-3.5" /><span>{noticia.viewsCount.toLocaleString()} visualizações</span></div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => { setNoticiaSelecionada(noticia); setModalAberto(true); }}
                      className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-1.5 text-sm">
                      <Eye className="w-4 h-4" /> Ver
                    </button>
                    <button onClick={() => {
                      setNoticiaEditando(noticia);
                      setFormEditarNoticia({
                        title:            noticia.title,
                        subtitle:         noticia.subtitle ?? '',
                        summary:          noticia.summary,
                        content:          noticia.content,
                        featuredImageUrl: noticia.featuredImageUrl ?? '',
                        externalUrl:      noticia.externalUrl ?? '',
                        category:         noticia.category,
                        galleryImages:    noticia.metadata?.galleryImages ?? [],
                      });
                      setModalEditarNoticia(true);
                    }}
                      className="px-3 py-2 rounded-lg border-2 border-amber-300 text-amber-600 hover:bg-amber-50 transition-all text-sm font-bold"
                      title="Editar">
                      <Edit className="w-4 h-4" />
                    </button>
                    {(noticia.status === 'draft' || noticia.status === 'archived') && (
                      <button onClick={() => handlePublicar(noticia.id)} disabled={salvando}
                        className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-all disabled:opacity-50 flex items-center gap-1.5 text-sm font-bold" title="Publicar">
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {noticia.status === 'published' && (
                      <button onClick={() => handleArquivar(noticia.id)} disabled={salvando}
                        className="px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-all disabled:opacity-50 flex items-center gap-1.5 text-sm font-bold" title="Arquivar">
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => handleDeletar(noticia.id, noticia.title)} disabled={salvando}
                      className="px-3 py-2 rounded-lg border-2 border-gray-300 text-red-600 hover:bg-red-50 transition-all disabled:opacity-50" title="Deletar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && noticiasFiltradas.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-semibold">Nenhuma publicação encontrada</p>
        </div>
      )}

      {/* ── MODAL DETALHES ───────────────────────────────────────────────── */}
      {modalAberto && noticiaSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-2xl font-black text-gray-900">Detalhes da Publicação</h3>
              <button onClick={() => setModalAberto(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              {noticiaSelecionada.featuredImageUrl && (
                <img src={noticiaSelecionada.featuredImageUrl} alt={noticiaSelecionada.title} className="w-full h-64 object-cover rounded-xl" />
              )}
              <div className="flex gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${getCategoriaConfig(noticiaSelecionada.category).bg} ${getCategoriaConfig(noticiaSelecionada.category).text} ${getCategoriaConfig(noticiaSelecionada.category).border}`}>
                  {getCategoriaConfig(noticiaSelecionada.category).label}
                </span>
                {getStatusBadge(noticiaSelecionada.status)}
              </div>
              <h4 className="text-3xl font-black text-gray-900">{noticiaSelecionada.title}</h4>
              {noticiaSelecionada.subtitle && <p className="text-xl text-gray-700 font-semibold">{noticiaSelecionada.subtitle}</p>}
              <p className="text-gray-600 border-l-4 border-blue-500 pl-4 italic">{noticiaSelecionada.summary}</p>

              {/* Galeria no modal detalhes */}
              {(noticiaSelecionada.metadata?.galleryImages ?? []).length > 0 && (
                <div>
                  <p className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2"><Image className="w-4 h-4" /> Galeria de Imagens</p>
                  <div className="grid grid-cols-3 gap-2">
                    {(noticiaSelecionada.metadata?.galleryImages ?? []).map((url, i) => (
                      <img key={i} src={url} alt={`gallery-${i}`} className="rounded-xl w-full aspect-video object-cover border border-gray-200" />
                    ))}
                  </div>
                </div>
              )}

              <div className="prose max-w-none">
                <h5 className="text-lg font-bold text-gray-900 mb-2">Conteúdo:</h5>
                <div className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: noticiaSelecionada.content }} />
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                {[
                  { label:'Autor',          value:noticiaSelecionada.author?.name ?? 'Admin' },
                  { label:'Publicado em',   value:noticiaSelecionada.publishedAt ? new Date(noticiaSelecionada.publishedAt).toLocaleDateString('pt-BR') : '—' },
                  { label:'Visualizações',  value:noticiaSelecionada.viewsCount.toLocaleString() },
                  { label:'Likes',          value:String(noticiaSelecionada.likesCount) },
                ].map(f => (
                  <div key={f.label}><p className="text-sm text-gray-600 mb-1">{f.label}</p><p className="font-bold text-gray-900">{f.value}</p></div>
                ))}
              </div>
              {noticiaSelecionada.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {noticiaSelecionada.tags.map((t, i) => <span key={i} className="px-3 py-1 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold">#{t}</span>)}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setModalAberto(false)} className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">Fechar</button>
              {(noticiaSelecionada.status === 'draft' || noticiaSelecionada.status === 'archived') && (
                <button onClick={() => handlePublicar(noticiaSelecionada.id)} disabled={salvando}
                  className="flex-1 px-4 py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-60">
                  {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Publicar Agora
                </button>
              )}
              {noticiaSelecionada.status === 'published' && (
                <button onClick={() => handleArquivar(noticiaSelecionada.id)} disabled={salvando}
                  className="flex-1 px-4 py-3 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 flex items-center justify-center gap-2 disabled:opacity-60">
                  {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Archive className="w-5 h-5" />} Arquivar
                </button>
              )}
              <button onClick={() => handleDeletar(noticiaSelecionada.id, noticiaSelecionada.title)} disabled={salvando}
                className="px-4 py-3 rounded-xl border-2 border-red-300 text-red-600 hover:bg-red-50 font-bold disabled:opacity-60">
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CRIAR ──────────────────────────────────────────────────── */}
      {modalCriar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-2xl font-black text-gray-900">Nova Publicação</h3>
              <button onClick={() => setModalCriar(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>

            <div className="p-6 space-y-6">
              {/* Imagem de Capa */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Imagem de Capa</label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-500 transition-all">
                  {novaNoticia.featuredImageUrl ? (
                    <div className="relative">
                      <img src={novaNoticia.featuredImageUrl} alt="Preview" className="w-full h-52 object-cover rounded-lg" />
                      <button onClick={() => setNovaNoticia(prev => ({ ...prev, featuredImageUrl: '' }))}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                      <button onClick={() => fileInputRef.current?.click()} disabled={uploadingImage}
                        className="px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 flex items-center gap-2 mx-auto disabled:opacity-50">
                        {uploadingImage ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                        {uploadingImage ? 'Enviando...' : 'Upload da Capa'}
                      </button>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 border-t border-gray-200" />
                        <span className="text-xs text-gray-400">ou cole uma URL</span>
                        <div className="flex-1 border-t border-gray-200" />
                      </div>
                      <div className="flex gap-2">
                        <input type="url" placeholder="https://exemplo.com/capa.jpg" value={novaNoticia.featuredImageUrl}
                          onChange={e => setNovaNoticia(p => ({ ...p, featuredImageUrl: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Título */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Título *</label>
                <input value={novaNoticia.title} onChange={e => setNovaNoticia(p => ({ ...p, title: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 font-semibold"
                  placeholder="Mínimo 10 caracteres" maxLength={200} />
                <p className={`text-xs mt-1 ${novaNoticia.title.length < 10 && novaNoticia.title.length > 0 ? 'text-red-500' : 'text-gray-500'}`}>
                  {novaNoticia.title.length}/200
                </p>
              </div>

              {/* Subtítulo */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Subtítulo</label>
                <input value={novaNoticia.subtitle} onChange={e => setNovaNoticia(p => ({ ...p, subtitle: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900"
                  placeholder="Subtítulo opcional" maxLength={500} />
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Categoria *</label>
                <select value={novaNoticia.category} onChange={e => setNovaNoticia(p => ({ ...p, category: e.target.value as NoticiaCategoria }))}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
                  <option value="announcement">Comunicado</option>
                  <option value="event">Evento</option>
                  <option value="tutorial">Tutorial</option>
                  <option value="industry_news">Notícia</option>
                  <option value="company_news">Empresa</option>
                  <option value="member_spotlight">Destaque</option>
                </select>
              </div>

              {/* Link Externo */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Link de Referência <span className="text-gray-400 font-normal">(opcional)</span></label>
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input value={novaNoticia.externalUrl} onChange={e => setNovaNoticia(p => ({ ...p, externalUrl: e.target.value }))}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900"
                    placeholder="https://exemplo.com/noticia-original" type="url" />
                </div>
              </div>

              {/* Resumo */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Resumo</label>
                <textarea value={novaNoticia.summary} onChange={e => setNovaNoticia(p => ({ ...p, summary: e.target.value }))}
                  rows={3} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none text-gray-900"
                  placeholder="Breve resumo que aparecerá na listagem..." maxLength={500} />
              </div>

              {/* Conteúdo */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Conteúdo Completo *</label>
                <textarea value={novaNoticia.content} onChange={e => setNovaNoticia(p => ({ ...p, content: e.target.value }))}
                  rows={10} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none text-gray-900 font-mono text-sm"
                  placeholder="Conteúdo completo (suporta HTML básico)" />
              </div>

              {/* Galeria de Imagens */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <ImagePlus className="w-4 h-4 text-indigo-600" />
                  Galeria de Imagens
                  <span className="text-xs font-normal text-gray-400">(opcional — aparece no corpo da notícia)</span>
                </label>
                <GaleriaUpload
                  images={novaNoticia.galleryImages}
                  onChange={imgs => setNovaNoticia(p => ({ ...p, galleryImages: imgs }))}
                />
              </div>

              {/* Autor */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Autor <span className="text-gray-400 font-normal">(opcional)</span></label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input value={novaNoticia.authorName} onChange={e => setNovaNoticia(p => ({ ...p, authorName: e.target.value }))}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900"
                    placeholder="Nome do autor ou redator" maxLength={100} />
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Tags</label>
                <input value={novaNoticia.tags.join(', ')}
                  onChange={e => setNovaNoticia(p => ({ ...p, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900"
                  placeholder="beleza, cabeleireiro, tendências (separadas por vírgula)" />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setModalCriar(false)} className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">Cancelar</button>
              <button onClick={handleCriar} disabled={salvando || novaNoticia.title.length < 10 || !novaNoticia.content.trim()}
                className="flex-1 px-4 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-60">
                {salvando ? <><Loader2 className="w-5 h-5 animate-spin" /> Publicando...</> : <><Send className="w-5 h-5" /> Publicar Agora</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL EDITAR NOTÍCIA ─────────────────────────────────────────── */}
      {modalEditar && noticiaEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto shadow-2xl">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-xl font-black text-gray-900">Editar Publicação</h3>
                <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{noticiaEditando.title}</p>
              </div>
              <button onClick={() => setModalEditarNoticia(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              {[
                { label:'Título *',       key:'title',            rows:1 },
                { label:'Subtítulo',      key:'subtitle',         rows:1 },
                { label:'Resumo',         key:'summary',          rows:3 },
                { label:'URL da Capa',    key:'featuredImageUrl', rows:1 },
                { label:'Link Externo',   key:'externalUrl',      rows:1 },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-sm font-bold text-gray-700 mb-1">{f.label}</label>
                  {f.rows > 1 ? (
                    <textarea rows={f.rows} value={(formEditarNoticia as any)[f.key] ?? ''}
                      onChange={e => setFormEditarNoticia((p: any) => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl resize-none text-gray-900 focus:ring-2 focus:ring-blue-500" />
                  ) : (
                    <input value={(formEditarNoticia as any)[f.key] ?? ''}
                      onChange={e => setFormEditarNoticia((p: any) => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500" />
                  )}
                </div>
              ))}

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Categoria</label>
                <select value={formEditarNoticia.category ?? 'announcement'}
                  onChange={e => setFormEditarNoticia(p => ({ ...p, category: e.target.value as NoticiaCategoria }))}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500">
                  <option value="announcement">Comunicado</option>
                  <option value="event">Evento</option>
                  <option value="tutorial">Tutorial</option>
                  <option value="industry_news">Notícia</option>
                  <option value="company_news">Empresa</option>
                  <option value="member_spotlight">Destaque</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Conteúdo completo *</label>
                <textarea rows={8} value={formEditarNoticia.content ?? ''}
                  onChange={e => setFormEditarNoticia(p => ({ ...p, content: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl resize-none text-gray-900 font-mono text-sm focus:ring-2 focus:ring-blue-500" />
              </div>

              {/* Galeria no modal editar */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <ImagePlus className="w-4 h-4 text-indigo-600" />
                  Galeria de Imagens
                  <span className="text-xs font-normal text-gray-400">(aparece no corpo da notícia)</span>
                </label>
                <GaleriaUpload
                  images={formEditarNoticia.galleryImages ?? []}
                  onChange={imgs => setFormEditarNoticia(p => ({ ...p, galleryImages: imgs }))}
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setModalEditarNoticia(false)} className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">Cancelar</button>
              <button onClick={handleSalvarEdicaoNoticia} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-60">
                {salvando ? <Loader2 className="w-5 h-5 animate-spin"/> : <CheckCircle className="w-5 h-5"/>}
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default GerenciamentoNoticiasArtigos;